import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    /** UMD-глобал @vkid/sdk. Именно VKIDSDK — см. getVkSdk(). */
    VKIDSDK?: any;
    /** Легаси-имя из старых сборок SDK, оставлено как запасной вариант. */
    VKID?: any;
  }
}

/**
 * UMD-бандл @vkid/sdk вешает себя на `window.VKIDSDK`, а не на `window.VKID`.
 * Из-за неверного имени ожидание SDK всегда истекало по таймауту, и экран входа
 * показывал «Ошибка входа через VK» ещё до клика по кнопке.
 */
function getVkSdk(): any | undefined {
  return window.VKIDSDK ?? window.VKID;
}

const VK_APP_ID = Number(import.meta.env.VITE_VK_APP_ID) || 54747482;
/**
 * По умолчанию — текущий origin: на dev.forruss.ru зашитый прод-адрес давал
 * несовпадение redirect_uri. Домен всё равно должен быть добавлен в настройки
 * приложения VK ID.
 */
const VK_REDIRECT_URI =
  (import.meta.env.VITE_VK_REDIRECT_URI as string) ||
  (typeof window !== "undefined" ? `${window.location.origin}/app` : "https://forruss.ru/app");

export interface VKLoginResult {
  vkId: string;
  accessToken: string;
  email?: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  phone?: string;
}

/**
 * Обмен кода VK ID на профиль и токен. Один и тот же для виджета (Callback) и для полного возврата
 * на redirectUrl (`?code&device_id`), когда SDK увёл страницу целиком.
 */
export async function vkResultFromCode(code: string, deviceId: string): Promise<VKLoginResult> {
  const VKID = getVkSdk();
  const exchangeResult = await VKID.Auth.exchangeCode(code, deviceId);
  // exchangeCode() НЕ возвращает поле `user` — только access_token и числовой user_id;
  // профиль отдаёт отдельный вызов userInfo(accessToken).
  const accessToken = exchangeResult.access_token || "";
  const userInfoResult = await VKID.Auth.userInfo(accessToken);
  const user = userInfoResult.user || {};
  return {
    vkId: String(exchangeResult.user_id || ""),
    accessToken,
    email: user.email || undefined,
    firstName: user.first_name || "",
    lastName: user.last_name || "",
    avatar: user.avatar || undefined,
    phone: user.phone || undefined,
  };
}

/** Дожидается загрузки SDK и инициализирует его (для обмена кода без отрисовки виджета). */
export async function initVkSdkForReturn(): Promise<void> {
  for (let i = 0; i < 30 && !getVkSdk()?.Config?.init; i++) await new Promise((r) => setTimeout(r, 100));
  const VKID = getVkSdk();
  if (!VKID?.Config?.init) throw new Error("VK ID SDK не загрузился");
  VKID.Config.init({
    app: VK_APP_ID,
    redirectUrl: VK_REDIRECT_URI,
    responseMode: VKID.ConfigResponseMode.Callback,
    source: VKID.ConfigSource.LOWCODE,
    scope: "",
  });
}

interface VKLoginButtonProps {
  onSuccess: (data: VKLoginResult) => void;
  /**
   * `phase` = "init" — виджет не поднялся сам по себе (SDK, домен, таймаут);
   * пользователь ничего не нажимал, и показывать ему ошибку не нужно.
   * `phase` = "login" — сбой после нажатия, о таком сообщаем.
   */
  onError?: (error: any, phase: "init" | "login") => void;
}

export const VKLoginButton: React.FC<VKLoginButtonProps> = ({ onSuccess, onError }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  /** Нажимал ли пользователь по виджету — отделяет «сломалось само» от «сломалось при входе». */
  const interactedRef = useRef(false);
  /**
   * `onSuccess`/`onError` приходят из родителя не через useCallback, поэтому при
   * каждом ре-рендере родителя (например, возврат фокуса на вкладку после
   * OAuth-редиректа в VK) это новая ссылка на функцию — эффект ниже перезапускался
   * бы и вызывал render() повторно В ТОТ ЖЕ containerRef, не убирая старый виджет:
   * на экране появлялась вторая кнопка «Войти с VK ID» поверх первой, а в момент
   * подмены старая на миг пропадала. Рендерим виджет ровно один раз за время
   * жизни компонента — эффект ниже это гарантирует.
   */
  const renderedOnceRef = useRef(false);

  useEffect(() => {
    // Wait for VKID SDK to load (it's loaded via <script> in index.html)
    let attempts = 0;
    const maxAttempts = 30; // 3 seconds max
    const checkInterval = setInterval(() => {
      attempts++;
      if (getVkSdk()?.Config?.init) {
        clearInterval(checkInterval);
        setSdkReady(true);
        return;
      }
      if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        setUnavailable(true);
        onError?.(new Error("VK ID SDK не загрузился"), "init");
      }
    }, 100);

    return () => clearInterval(checkInterval);
  }, [onError]);

  useEffect(() => {
    if (!sdkReady || !containerRef.current || renderedOnceRef.current) return;

    const VKID = getVkSdk();
    if (!VKID) return;

    renderedOnceRef.current = true;

    try {
      VKID.Config.init({
        app: VK_APP_ID,
        redirectUrl: VK_REDIRECT_URI,
        responseMode: VKID.ConfigResponseMode.Callback,
        source: VKID.ConfigSource.LOWCODE,
        scope: "",
      });

      const oAuth = new VKID.OAuthList();
      oAuth
        .render({
          container: containerRef.current!,
          styles: {
            borderRadius: 50,
            height: 40,
          },
          oauthList: ["vkid", "mail_ru"],
        })
        .on(VKID.WidgetEvents.ERROR, (error: any) => {
          console.error("VK OAuthList error:", error);
          // Виджет не смог отрисоваться/подняться — прячем его, а не пугаем
          // пользователя красной плашкой над формой входа по email.
          if (!interactedRef.current) setUnavailable(true);
          onError?.(error, interactedRef.current ? "login" : "init");
        })
        .on(VKID.OAuthListInternalEvents.LOGIN_SUCCESS, async (payload: any) => {
          try {
            onSuccess(await vkResultFromCode(payload.code, payload.device_id));
          } catch (e) {
            console.error("VK exchangeCode/userInfo error:", e);
            onError?.(e, "login");
          }
        });
    } catch (e) {
      console.error("VK OneTap init error:", e);
      setUnavailable(true);
      onError?.(e, "init");
    }
  }, [sdkReady, onSuccess, onError]);

  if (unavailable || !sdkReady) return null;

  return <div ref={containerRef} onClickCapture={() => { interactedRef.current = true; }} />;
};

/** VK logo icon matching brand style (same size as Yandex icon). */
function VkIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0 23.04C0 12.1788 0 6.74826 3.37413 3.37413C6.74826 0 12.1788 0 23.04 0H24.96C35.8212 0 41.2517 0 44.6259 3.37413C48 6.74826 48 12.1788 48 23.04V24.96C48 35.8212 48 41.2517 44.6259 44.6259C41.2517 48 35.8212 48 24.96 48H23.04C12.1788 48 6.74826 48 3.37413 44.6259C0 41.2517 0 35.8212 0 24.96V23.04Z"
        fill="#0077FF"
      />
      <path
        d="M25.54 34.58C14.6 34.58 8.36 27.08 8.1 14.6H13.58C13.76 23.76 18.58 27.64 22.36 28.44V14.6H27.52V22.5C31.24 22.1 35.12 18.04 36.36 14.6H41.52C39.94 20.98 35.06 25.7 31.34 27.34C35.06 28.74 40.02 33 41.72 38.82H36C34.7 34.62 31.42 31.38 27.52 30.9V38.82H25.54V34.58Z"
        fill="white"
      />
    </svg>
  );
}